import React from 'react';
import AiLabel from './AiLabel';
import { AlertTriangle, Calendar, ExternalLink, Wallet, Target, Users, Zap, Youtube } from 'lucide-react';
import {
  formatCount, formatIndex, formatShare, groupHighOverlap, isFinderUrl, overlapHeadline,
} from '../lib/audienceProfile';

/**
 * The plan summary, laid out.
 *
 * The step first shipped its summary as markdown, which rendered as a wall of
 * bullets inside a chat bubble — accurate but not something to put in front of
 * a client. The backend now sends the same facts as a `json:plan-summary`
 * payload and this lays them out.
 *
 * `markdown` rides along inside the payload, so a shape we cannot render still
 * shows the content rather than an empty panel.
 */
export interface ProfileMetric {
  id: string;
  name: string;
  index: number;
  audience_share: number;
  subscriber_count?: number;
  median_monthly_inventory?: number;
}

export interface OverlapPair {
  smaller: string;
  larger: string;
  shared: number;
  share_of_smaller: number;
}

/** How much the approved audiences overlap, from union reach. */
export interface AudienceOverlap {
  status: 'available' | 'unavailable' | 'not_applicable';
  reason?: string;
  unpriced?: string[];
  pairs?: OverlapPair[];
  high_overlap?: OverlapPair[];
  summed_reach?: number;
  unique_reach?: number;
}

/** Who the approved audiences are, from Audience Insights composition. */
export interface AudienceProfile {
  status: 'available' | 'unavailable';
  reason?: string;
  country?: string;
  based_on?: string[];
  excluded?: string[];
  channels?: ProfileMetric[];
  lineups?: ProfileMetric[];
  related_audiences?: Array<ProfileMetric & { type: string; targetable: boolean }>;
  overlap?: AudienceOverlap;
  finder_report_url?: string;
  topics?: Array<{ name: string; kind: 'topic' | 'category'; relevance: 'high' | 'medium' }>;
  demographics?: {
    age?: ProfileMetric[];
    gender?: ProfileMetric[];
    parental_status?: ProfileMetric[];
  };
}

export interface PlanSummaryPayload {
  campaign?: Record<string, string>;
  targeting?: Record<string, string>;
  audiences?: Array<{ name: string; type: string; targetable: boolean }>;
  objective?: {
    name?: string;
    why?: string;
    platform?: string;
    settings?: string[][];
  };
  has_objective?: boolean;
  audience_profile?: AudienceProfile;
  editable?: string[];
  markdown?: string;
}

/** Pull the payload out of a streamed message. Null when it is not there yet. */
export const PLAN_SUMMARY_FENCE = /```json:plan-summary\n([\s\S]*?)\n```/;

export const parsePlanSummary = (text: string): PlanSummaryPayload | null => {
  const match = text.match(PLAN_SUMMARY_FENCE);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
};

/**
 * Whether a whole payload block arrived but could not be read.
 *
 * Distinguishes "still streaming" (hide it, it is half-written) from "arrived
 * broken" (say so, rather than leaving an empty panel).
 */
export const hasUnreadablePlanSummary = (text: string): boolean =>
  PLAN_SUMMARY_FENCE.test(text) && parsePlanSummary(text) === null;

/** The message with the payload block removed, so prose can render around it. */
export const stripPlanSummary = (text: string): string =>
  text
    .replace(PLAN_SUMMARY_FENCE, '')
    // Removing the block leaves the blank lines that surrounded it, which
    // render as extra paragraph breaks between the prose above and below.
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const CARD: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '16px 18px',
  marginBottom: '14px',
};

const SECTION_TITLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#6b7280',
  margin: '0 0 12px',
};

const LABEL: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#9ca3af',
  marginBottom: '2px',
};

const VALUE: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  lineHeight: 1.35,
  wordBreak: 'break-word',
};

const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div>
    <div style={LABEL}>{label}</div>
    <div style={{ ...VALUE, color: value === 'Not set' ? '#9ca3af' : VALUE.color }}>
      {value || 'Not set'}
    </div>
  </div>
);

const Grid: React.FC<{ children: React.ReactNode; min?: number }> = ({ children, min = 130 }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    gap: '14px 18px',
  }}>
    {children}
  </div>
);

const TYPE_COLOURS: Record<string, { bg: string; fg: string }> = {
  affinity: { bg: '#ede9fe', fg: '#6d28d9' },
  'in-market': { bg: '#dcfce7', fg: '#15803d' },
  'life event': { bg: '#fef3c7', fg: '#b45309' },
};

const RELATED_COLOURS: Record<string, { bg: string; fg: string }> = {
  affinity: TYPE_COLOURS.affinity,
  in_market: TYPE_COLOURS['in-market'],
};

/** One demographic as a row of share bars, so the split reads at a glance. */
const ShareBars: React.FC<{ label: string; rows?: ProfileMetric[] }> = ({ label, rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div style={LABEL}>{label}</div>
      {rows.map((r) => (
        <div
          key={r.id}
          title={`${formatShare(r.audience_share)} of this audience · ${formatIndex(r.index)} the country average`}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', margin: '4px 0' }}
        >
          <span style={{ width: '82px', color: '#374151', flexShrink: 0 }}>{r.name}</span>
          <span style={{ flex: 1, height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
            <span style={{
              display: 'block', height: '100%', borderRadius: '3px', background: '#8b5cf6',
              width: formatShare(Math.min(1, Math.max(0, r.audience_share))),
            }} />
          </span>
          <span style={{ width: '34px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>
            {formatShare(r.audience_share)}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * The approved audiences, described: the channels and lineups they watch,
 * the audiences they over-index on that were not approved, and their
 * demographic split. Indexes compare with the country as a whole.
 */
const AudienceProfileSection: React.FC<{ profile: AudienceProfile }> = ({ profile }) => {
  if (profile.status !== 'available') {
    return (
      <div style={CARD}>
        <h4 style={SECTION_TITLE}><Youtube size={13} /> Audience profile</h4>
        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
          {profile.reason || 'Not available for this session.'}
        </div>
      </div>
    );
  }
  const channels = profile.channels || [];
  const lineups = profile.lineups || [];
  const related = profile.related_audiences || [];
  const demo = profile.demographics || {};
  const topics = profile.topics || [];

  return (
    <div style={CARD}>
      <h4 style={SECTION_TITLE}><Youtube size={13} /> Audience profile</h4>
      <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5, marginBottom: '14px' }}>
        Who your approved audiences are{profile.country ? ` in ${profile.country}` : ''}.
        {' '}“2.0×” means twice as likely as the average viewer there.
        {profile.excluded && profile.excluded.length > 0 && (
          <> Not included: {profile.excluded.join(', ')}.</>
        )}
      </div>

      {channels.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={LABEL}>Channels they watch</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
            {channels.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '13px' }}>
                <a
                  href={`https://www.youtube.com/channel/${encodeURIComponent(c.id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#111827', fontWeight: 600, textDecoration: 'none' }}
                >
                  {c.name}
                </a>
                {formatCount(c.subscriber_count) && (
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {formatCount(c.subscriber_count)} subscribers
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 700, color: '#6d28d9' }}>
                  {formatIndex(c.index)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={LABEL}>Audiences to consider</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
            {related.map((a) => {
              const colour = RELATED_COLOURS[a.type] || { bg: '#f3f4f6', fg: '#374151' };
              return (
                <span
                  key={`${a.type}-${a.id}`}
                  title={a.targetable ? `${formatIndex(a.index)} the average` : 'Insight only, not targetable'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 10px', borderRadius: '999px',
                    background: a.targetable ? colour.bg : '#fff',
                    color: a.targetable ? colour.fg : '#9ca3af',
                    border: a.targetable ? '1px solid transparent' : '1px dashed #d1d5db',
                    fontSize: '12px', fontWeight: 600,
                  }}
                >
                  {a.name}
                  <span style={{ fontWeight: 700, opacity: 0.8 }}>{formatIndex(a.index)}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {lineups.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={LABEL}>YouTube lineups</div>
          <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
            {lineups.map((l) => `${l.name} (${formatShare(l.audience_share)})`).join(' · ')}
          </div>
        </div>
      )}

      {/* Themes Google matched to the brief: a starting point for creative
          and for which content to place against. */}
      {topics.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={LABEL}>Content themes</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
            {topics.map((t) => (
              <span
                key={t.name}
                title={t.kind === 'category' ? 'Content category' : 'Topic'}
                style={{
                  fontSize: '12px', padding: '3px 8px', borderRadius: '999px',
                  background: t.relevance === 'high' ? '#ede9fe' : '#f3f4f6',
                  color: t.relevance === 'high' ? '#5b21b6' : '#4b5563',
                }}
              >
                {t.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Grid min={180}>
        <ShareBars label="Age" rows={demo.age} />
        <ShareBars label="Gender" rows={demo.gender} />
        <ShareBars label="Parents" rows={demo.parental_status} />
      </Grid>

      {/* Google's own report on the same audiences, to share with a client.
          Only a link to Google Ads is rendered. */}
      {isFinderUrl(profile.finder_report_url) && (
        <a
          href={profile.finder_report_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '14px',
            fontSize: '13px', fontWeight: 600, color: '#6d28d9', textDecoration: 'none',
          }}
        >
          <ExternalLink size={13} /> Open the full report in Google Insights Finder
        </a>
      )}
    </div>
  );
};

/**
 * Whether the approved audiences are mostly the same people. Two audiences
 * that share most of their viewers add little reach together and compete for
 * the same impressions, so the pairs above the threshold are called out.
 */
const OverlapNote: React.FC<{ overlap?: AudienceOverlap }> = ({ overlap }) => {
  if (!overlap || overlap.status === 'not_applicable') return null;
  if (overlap.status !== 'available') {
    return (
      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
        {overlap.reason || 'Overlap is not available for this session.'}
      </div>
    );
  }
  const headline = overlapHeadline(overlap.summed_reach || 0, overlap.unique_reach || 0);
  const high = groupHighOverlap(overlap.high_overlap || []);
  if (!headline && high.length === 0) return null;
  return (
    <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
      <div style={LABEL}>Overlap</div>
      {headline && (
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5, marginBottom: high.length ? '8px' : 0 }}>
          {headline}
        </div>
      )}
      {high.map((g) => (
        <div
          key={g.audience}
          style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12px', color: '#92400e', margin: '4px 0', lineHeight: 1.5 }}
        >
          <AlertTriangle size={14} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
          <span>
            <strong>{g.audience}</strong> is mostly inside{' '}
            {g.within.map((w, i) => (
              <React.Fragment key={w.name}>
                {i > 0 && (i === g.within.length - 1 ? ' and ' : ', ')}
                {w.name} ({formatShare(w.share)})
              </React.Fragment>
            ))}
            , so it adds little reach of its own.
          </span>
        </div>
      ))}
      {overlap.unpriced && overlap.unpriced.length > 0 && (
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
          Not sized, so not included: {overlap.unpriced.join(', ')}.
        </div>
      )}
    </div>
  );
};

const PlanSummaryCard: React.FC<{ payload: PlanSummaryPayload }> = ({ payload }) => {
  const campaign = payload.campaign || {};
  const targeting = payload.targeting || {};
  const audiences = payload.audiences || [];
  const objective = payload.objective || {};

  return (
    <div>
      {/* Campaign */}
      <div style={CARD}>
        <h4 style={SECTION_TITLE}><Wallet size={13} /> Campaign</h4>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
            {campaign.brand || 'Not set'}
          </span>
          {campaign.platform && campaign.platform !== 'Not set' && (
            <span style={{
              fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '5px',
              background: '#f3f4f6', color: '#374151', letterSpacing: '0.04em',
            }}>
              {campaign.platform}
            </span>
          )}
        </div>
        <Grid>
          <Field label="Budget" value={campaign.budget} />
          <Field label="Dates" value={campaign.dates} />
          <Field label="Duration" value={campaign.duration} />
        </Grid>
        {campaign.goal && campaign.goal !== 'Not set' && (
          <div style={{
            marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f3f4f6',
          }}>
            <div style={LABEL}>Goal</div>
            <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
              {campaign.goal}
            </div>
          </div>
        )}
      </div>

      {/* Targeting */}
      <div style={CARD}>
        <h4 style={SECTION_TITLE}><Calendar size={13} /> People and place</h4>
        {/* Gemini reads these from the brief when it is approved. */}
        <AiLabel text="Read from the brief by AI · check and edit" />
        <Grid min={110}>
          <Field label="Market" value={targeting.market} />
          <Field label="Age" value={targeting.age} />
          <Field label="Gender" value={targeting.gender} />
          <Field label="Devices" value={targeting.devices} />
        </Grid>
      </div>

      {/* Audiences */}
      <div style={CARD}>
        <h4 style={SECTION_TITLE}>
          <Users size={13} /> Approved audiences
          {audiences.length > 0 && (
            <span style={{ color: '#9ca3af', fontWeight: 600 }}>· {audiences.length}</span>
          )}
        </h4>
        {audiences.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>None approved yet.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {audiences.map((a, i) => {
              const colour = TYPE_COLOURS[a.type] || { bg: '#f3f4f6', fg: '#374151' };
              return (
                <span
                  key={`${a.name}-${i}`}
                  title={a.targetable ? a.type : `${a.type} — insight only, not targetable`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 10px', borderRadius: '999px',
                    background: a.targetable ? colour.bg : '#fff',
                    color: a.targetable ? colour.fg : '#9ca3af',
                    border: a.targetable ? '1px solid transparent' : '1px dashed #d1d5db',
                    fontSize: '12px', fontWeight: 600,
                  }}
                >
                  {a.name}
                  {!a.targetable && (
                    <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
                      INSIGHT ONLY
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        )}
        <OverlapNote overlap={payload.audience_profile?.overlap} />
      </div>

      {payload.audience_profile && <AudienceProfileSection profile={payload.audience_profile} />}

      {/* Objective */}
      <div style={{
        ...CARD,
        marginBottom: 0,
        ...(payload.has_objective === false
          ? { background: '#fffbeb', borderColor: '#fcd34d' }
          : {}),
      }}>
        <h4 style={SECTION_TITLE}><Target size={13} /> Objective</h4>
        {/* Worked out from brief signals (sector, funnel) that Gemini inferred. */}
        {payload.has_objective !== false && <AiLabel text="Based on brief signals read by AI · check before use" />}
        {payload.has_objective === false ? (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertTriangle size={18} style={{ color: '#b45309', flexShrink: 0, marginTop: '1px' }} />
            <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
              <strong>Not worked out for this session.</strong> Scenario planning needs it.
              Go back to the Brief step and check the budget, dates and goal are set.
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Zap size={16} style={{ color: '#8b5cf6' }} />
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                {objective.name}
              </span>
            </div>
            {objective.why && (
              <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5, marginBottom: '14px' }}>
                {objective.why}
              </div>
            )}
            {objective.settings && objective.settings.length > 0 && (
              <div style={{ paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                {objective.platform && (
                  <div style={{ ...LABEL, marginBottom: '10px' }}>
                    {objective.platform} setup
                  </div>
                )}
                <Grid min={150}>
                  {objective.settings.map(([key, value]) => (
                    <Field key={key} label={key} value={value} />
                  ))}
                </Grid>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanSummaryCard;
